import React from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { TagLabel } from '@/components/ui/tag-label';
import { CircleNumber } from '@/components/ui/circle-number';
import {
  Pencil,
  Trash2,
  GripVertical,
  Tag,
  X,
  Archive,
  Inbox,
  CheckCircle,
  Circle,
  Star,
  Ban,
  Zap,
} from 'lucide-react';
import { Action, getActionTypeMetadata, ActionType } from '@/types/action';

interface ActionCardProps {
  action: Action;
  onEdit: () => void;
  onDelete: () => void;
  dragHandleProps?: any;
}

const actionIcons: Record<ActionType, React.ComponentType<{ className?: string }>> = {
  apply_label: Tag,
  remove_label: X,
  skip_inbox: Archive,
  send_to_inbox: Inbox,
  mark_as_read: CheckCircle,
  mark_as_unread: Circle,
  mark_as_important: Star,
  mark_as_unimportant: Circle,
  add_to_blocklist: Ban,
  delete: Trash2,
};

const actionTypeLabels: Record<ActionType, string> = {
  apply_label: 'Apply Label',
  remove_label: 'Remove Label',
  skip_inbox: 'Skip Inbox',
  send_to_inbox: 'Send to Inbox',
  mark_as_read: 'Mark as Read',
  mark_as_unread: 'Mark as Unread',
  mark_as_important: 'Mark as Important',
  mark_as_unimportant: 'Mark as Unimportant',
  add_to_blocklist: 'Add the sender\'s email to your block list.',
  delete: 'Delete',
};

export function ActionCard({
  action,
  onEdit,
  onDelete,
  dragHandleProps,
}: ActionCardProps) {
  const actionTypeMetadata = getActionTypeMetadata(action.actionType);
  const IconComponent = actionIcons[action.actionType] || Zap;

  const getParameterSummary = () => {
    if (!action.parameters || Object.keys(action.parameters).length === 0) {
      return actionTypeMetadata?.description || '';
    }

    const parts: string[] = [];
    if (action.parameters.label_name) {
      parts.push(`Label: ${action.parameters.label_name}`);
    }

    return parts.join(' • ') || actionTypeMetadata?.description || '';
  };

  return (
    <Card className="p-4 bg-white hover:bg-slate-50 dark:hover:bg-gray-900 transition-colors cursor-pointer group">
      <div className="flex items-start gap-2 sm:gap-3">
        {/* Drag Handle */}
        <div
          {...dragHandleProps}
          className="cursor-grab active:cursor-grabbing text-slate-400 hover:text-slate-600 self-center"
        >
          <GripVertical className="h-5 w-5" />
        </div>

        {/* Action Order Number */}
        <CircleNumber number={action.order} className="shrink-0 self-center" />

        {/* Action Details */}
        <div className="flex-1 min-w-0" onClick={onEdit}>
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-1">
            <TagLabel
              className="shrink-0 self-start text-xs py-1 px-2 rounded"
              leftIcon={<IconComponent className="h-3 w-3" />}
            >
              {actionTypeLabels[action.actionType] || actionTypeMetadata?.label || action.actionType}
            </TagLabel>
            <h3 className="font-semibold text-sm text-blue-950 dark:text-white w-full sm:w-auto sm:truncate break-words">
              {actionTypeMetadata?.label || action.actionType}
            </h3>
          </div>
          <p className="text-sm text-slate-500 truncate">
            {getParameterSummary()}
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onEdit();
            }}
          >
            <Pencil className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            className="text-red-600 hover:text-red-700 hover:bg-red-50"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </Card>
  );
}
